import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, IconButton, Collapse, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ItemCard, { ItemWithSource } from "../Cards/ItemCard";
import { useTranslation } from "react-i18next";
import { TaskStatusKey, MeetingStatusKey } from "../../utils/statusColors";

type StatusKey = "all" | TaskStatusKey | MeetingStatusKey;

interface Props {
  parentId: string;
  subtasks: ItemWithSource[];
  onRefresh: () => Promise<void>;
  getStatusConfig: (
    status: StatusKey,
    source: "task" | "meeting"
  ) => { color: string; translation: string };
  customNavigate?: (item: ItemWithSource) => string;
}

const SubtasksAccordion: React.FC<Props> = ({
  parentId,
  subtasks,
  onRefresh,
  getStatusConfig,
  customNavigate,
}) => {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();


  return (
    <Box
      sx={{
        borderRadius: 2,
        background: "#fff",
        mb: 2,
        minWidth: 280,
        maxWidth: 500,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          justifyContent: "space-between",
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <Typography
            variant="h4"
            sx={{ flex: 1, fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
          >
            {t("tasks.subtasks")}
          </Typography>
          <IconButton
            size="small"
            sx={{
              transition: "transform 0.2s",
              mr: 1,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              color: "grey.700",
            }}
            disableRipple
            tabIndex={-1}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <IconButton
          size="small"
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/task/new?parentId=${parentId}`);
          }}
          sx={{
            border: "1px solid #4467A5",
            borderRadius: "8px",
            bgcolor: "#4467A5",
            color: "#fff",
            "&:hover": { bgcolor: "#385894" },
          }}
        >
          <AddIcon />
        </IconButton>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 1.5 }}>
          {subtasks.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ fontSize: "0.95rem", py: 2, textAlign: "center" }}
            >
              {t("tasks.noSubtasks")}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {subtasks.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  getStatusConfig={getStatusConfig}
                  customNavigate={customNavigate}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default SubtasksAccordion;
